import { useState, useMemo } from "react";
import { format, isPast, isToday, isTomorrow, differenceInDays, parseISO, addDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { 
  Calendar, 
  Clock, 
  Plus, 
  CheckCircle2, 
  Circle, 
  Trash2, 
  Pencil,
  Video,
  Phone,
  Mail,
  FileText,
  AlertCircle,
  CalendarClock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  useClientReminders, 
  useCreateClientReminder, 
  useUpdateClientReminder, 
  useDeleteClientReminder,
  useToggleReminderComplete,
  ClientReminder 
} from "@/hooks/useClientReminders";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface Project {
  id: string;
  name: string;
  deadline: string | null;
  status: string | null;
}

interface ClientAgendaSectionProps {
  clientId: string;
  projects: Project[];
}

const reminderTypeConfig = {
  meeting: { icon: Video, label: "Reunião", color: "bg-primary/10 text-primary" },
  call: { icon: Phone, label: "Ligação", color: "bg-success/10 text-success" },
  email: { icon: Mail, label: "Email", color: "bg-warning/10 text-warning" },
  deadline: { icon: AlertCircle, label: "Prazo", color: "bg-destructive/10 text-destructive" },
  followup: { icon: CalendarClock, label: "Follow-up", color: "bg-muted text-muted-foreground" },
  other: { icon: FileText, label: "Outro", color: "bg-secondary text-secondary-foreground" },
};

interface ReminderFormData {
  title: string;
  description: string;
  reminder_date: string;
  reminder_time: string;
  reminder_type: string;
  project_id: string;
}

const initialFormData: ReminderFormData = {
  title: "",
  description: "",
  reminder_date: format(new Date(), "yyyy-MM-dd"),
  reminder_time: "",
  reminder_type: "meeting",
  project_id: "",
};

export function ClientAgendaSection({ clientId, projects }: ClientAgendaSectionProps) {
  const { toast } = useToast();
  const { data: reminders, isLoading } = useClientReminders(clientId);
  const createReminder = useCreateClientReminder();
  const updateReminder = useUpdateClientReminder();
  const deleteReminder = useDeleteClientReminder();
  const toggleComplete = useToggleReminderComplete();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState<ClientReminder | null>(null);
  const [formData, setFormData] = useState<ReminderFormData>(initialFormData);

  // Combine reminders with project deadlines
  const timelineItems = useMemo(() => {
    const items: Array<{
      id: string;
      type: 'reminder' | 'deadline';
      title: string;
      description: string | null;
      date: string;
      time: string | null;
      reminderType: string;
      isCompleted: boolean;
      projectName?: string;
      projectId?: string;
      reminder?: ClientReminder;
    }> = [];

    // Add reminders
    reminders?.forEach(reminder => {
      const project = projects.find(p => p.id === reminder.project_id);
      items.push({
        id: reminder.id,
        type: 'reminder',
        title: reminder.title,
        description: reminder.description,
        date: reminder.reminder_date,
        time: reminder.reminder_time,
        reminderType: reminder.reminder_type,
        isCompleted: reminder.is_completed,
        projectName: project?.name,
        projectId: reminder.project_id || undefined,
        reminder,
      });
    });

    // Add project deadlines (only active projects)
    projects.forEach(project => {
      if (project.deadline && project.status !== 'done') {
        items.push({
          id: `deadline-${project.id}`,
          type: 'deadline',
          title: `Prazo: ${project.name}`,
          description: null,
          date: project.deadline,
          time: null,
          reminderType: 'deadline',
          isCompleted: false,
          projectName: project.name,
          projectId: project.id,
        });
      }
    });

    // Sort by date
    return items.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
  }, [reminders, projects]);

  // Group by relative date
  const groupedItems = useMemo(() => {
    const groups: Record<string, typeof timelineItems> = {
      overdue: [],
      today: [],
      tomorrow: [],
      thisWeek: [],
      later: [],
      completed: [],
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    timelineItems.forEach(item => {
      if (item.isCompleted) {
        groups.completed.push(item);
        return;
      }

      const itemDate = parseISO(item.date);
      itemDate.setHours(0, 0, 0, 0);

      if (isPast(itemDate) && !isToday(itemDate)) {
        groups.overdue.push(item);
      } else if (isToday(itemDate)) {
        groups.today.push(item);
      } else if (isTomorrow(itemDate)) {
        groups.tomorrow.push(item);
      } else if (differenceInDays(itemDate, today) <= 7) {
        groups.thisWeek.push(item);
      } else {
        groups.later.push(item);
      }
    });

    return groups;
  }, [timelineItems]);

  const handleOpenDialog = (reminder?: ClientReminder) => {
    if (reminder) {
      setEditingReminder(reminder);
      setFormData({
        title: reminder.title,
        description: reminder.description || "",
        reminder_date: reminder.reminder_date,
        reminder_time: reminder.reminder_time || "",
        reminder_type: reminder.reminder_type,
        project_id: reminder.project_id || "",
      });
    } else {
      setEditingReminder(null);
      setFormData(initialFormData);
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Título é obrigatório",
      });
      return;
    }

    try {
      if (editingReminder) {
        await updateReminder.mutateAsync({
          id: editingReminder.id,
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          reminder_date: formData.reminder_date,
          reminder_time: formData.reminder_time || null,
          reminder_type: formData.reminder_type,
          project_id: formData.project_id || null,
        });
        toast({ title: "Lembrete atualizado!" });
      } else {
        await createReminder.mutateAsync({
          client_id: clientId,
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          reminder_date: formData.reminder_date,
          reminder_time: formData.reminder_time || null,
          reminder_type: formData.reminder_type,
          project_id: formData.project_id || null,
        });
        toast({ title: "Lembrete criado!" });
      }
      setIsDialogOpen(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível salvar o lembrete",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteReminder.mutateAsync({ id, clientId });
      toast({ title: "Lembrete removido!" });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível remover o lembrete",
      });
    }
  };

  const handleToggleComplete = async (id: string, currentStatus: boolean) => {
    try {
      await toggleComplete.mutateAsync({ id, is_completed: !currentStatus });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o lembrete",
      });
    }
  };

  const formatRelativeDate = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return "Hoje";
    if (isTomorrow(date)) return "Amanhã";
    return format(date, "dd 'de' MMMM", { locale: ptBR });
  };

  const renderTimelineItem = (item: typeof timelineItems[0]) => {
    const config = reminderTypeConfig[item.reminderType as keyof typeof reminderTypeConfig] || reminderTypeConfig.other;
    const Icon = config.icon;
    const isDeadline = item.type === 'deadline';
    const dateObj = parseISO(item.date);
    const isOverdue = isPast(dateObj) && !isToday(dateObj) && !item.isCompleted;

    return (
      <div
        key={item.id}
        className={cn(
          "flex items-start gap-3 p-3 rounded-lg border transition-all",
          item.isCompleted && "opacity-60 bg-muted/30",
          isOverdue && !isDeadline && "border-destructive/50 bg-destructive/5"
        )}
      >
        {/* Toggle complete button (only for reminders) */}
        {!isDeadline ? (
          <button
            onClick={() => handleToggleComplete(item.id, item.isCompleted)}
            className="mt-0.5 shrink-0"
          >
            {item.isCompleted ? (
              <CheckCircle2 className="h-5 w-5 text-success" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground hover:text-primary" />
            )}
          </button>
        ) : (
          <div className="mt-0.5 shrink-0">
            <AlertCircle className={cn("h-5 w-5", isOverdue ? "text-destructive" : "text-warning")} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={cn(
                "font-medium truncate",
                item.isCompleted && "line-through text-muted-foreground"
              )}>
                {item.title}
              </p>
              {item.description && (
                <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                  {item.description}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <Badge variant="outline" className={cn("text-xs gap-1", config.color)}>
                  <Icon className="h-3 w-3" />
                  {config.label}
                </Badge>
                {item.time && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {item.time.slice(0, 5)}
                  </span>
                )}
                {item.projectName && (
                  <Badge variant="secondary" className="text-xs">
                    {item.projectName}
                  </Badge>
                )}
              </div>
            </div>

            {/* Actions (only for reminders) */}
            {!isDeadline && (
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => handleOpenDialog(item.reminder)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => handleDelete(item.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGroup = (title: string, items: typeof timelineItems, variant: 'destructive' | 'warning' | 'default' = 'default') => {
    if (items.length === 0) return null;

    return (
      <div className="space-y-2">
        <h4 className={cn(
          "text-sm font-medium",
          variant === 'destructive' && "text-destructive",
          variant === 'warning' && "text-warning"
        )}>
          {title}
        </h4>
        <div className="space-y-2">
          {items.map(renderTimelineItem)}
        </div>
      </div>
    );
  };

  const totalPending = timelineItems.filter(i => !i.isCompleted).length;

  return (
    <>
      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Agenda
            {totalPending > 0 && (
              <Badge variant="secondary" className="ml-2">
                {totalPending} pendente{totalPending > 1 ? 's' : ''}
              </Badge>
            )}
          </CardTitle>
          <Button size="sm" onClick={() => handleOpenDialog()}>
            <Plus className="h-4 w-4 mr-1" />
            Novo
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : timelineItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum lembrete ou prazo agendado</p>
              <Button variant="link" onClick={() => handleOpenDialog()}>
                Criar primeiro lembrete
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {renderGroup("⚠️ Atrasado", groupedItems.overdue, 'destructive')}
              {renderGroup("Hoje", groupedItems.today, 'warning')}
              {renderGroup("Amanhã", groupedItems.tomorrow)}
              {renderGroup("Esta semana", groupedItems.thisWeek)}
              {renderGroup("Próximos", groupedItems.later)}
              {groupedItems.completed.length > 0 && (
                <details className="group">
                  <summary className="text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground">
                    Concluídos ({groupedItems.completed.length})
                  </summary>
                  <div className="mt-2 space-y-2">
                    {groupedItems.completed.map(renderTimelineItem)}
                  </div>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog for creating/editing reminders */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingReminder ? "Editar Lembrete" : "Novo Lembrete"}
            </DialogTitle>
            <DialogDescription>
              Agende reuniões, ligações ou lembretes de follow-up
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                placeholder="Ex: Reunião de kickoff"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="date">Data *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.reminder_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, reminder_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Horário</Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.reminder_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, reminder_time: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Tipo</Label>
              <Select
                value={formData.reminder_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, reminder_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="meeting">Reunião</SelectItem>
                  <SelectItem value="call">Ligação</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="followup">Follow-up</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {projects.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="project">Projeto (opcional)</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, project_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Nenhum</SelectItem>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                placeholder="Detalhes do lembrete..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={createReminder.isPending || updateReminder.isPending}
            >
              {(createReminder.isPending || updateReminder.isPending) ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
